/* External */
import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import { ScrubbingDirection, ScrubbingSpeed } from '@dtvgo/progress-bar';
import { useLiveProgressBarInfo } from '@dtvgo/player-utils-react';

/* Components */
import FocusableProgressBar from './FocusableProgressBar';

function LiveFocusableProgressBar({
  className,
  displaySeekableStartMessage,
  scrubbing,
  isScrubbing,
  onSeekingStart,
  onSeekingEnd,
  onSeekableStartReached,
  onSeekableEndReached,
  onKeyDown,
  onArrowPress,
  onBecameFocused,
  onBecameBlurred,
}) {
  const {
    progress, duration, seekable, handleSeek,
  } = useLiveProgressBarInfo();

  const seekableStartArrowPlacement = useMemo(() => {
    const percentage = seekable.start / duration;
    if (percentage <= 0.1) return 'left';
    if (percentage >= 0.85) return 'right';

    return 'center';
  }, [seekable.start, duration]);

  const shouldDisplaySeekableStartMessage = displaySeekableStartMessage && seekable.start > 0;
  const seekableStartDot = useMemo(() => shouldDisplaySeekableStartMessage && (
    <FocusableProgressBar.SeekableStartDot arrowPlacement={seekableStartArrowPlacement}>
      <FormattedMessage id="player.live.minToScrub.message" />
    </FocusableProgressBar.SeekableStartDot>
  ), [seekableStartArrowPlacement, shouldDisplaySeekableStartMessage]);

  return (
    <FocusableProgressBar
      className={className}
      progress={progress}
      duration={duration}
      seekable={seekable}
      scrubbing={scrubbing}
      isScrubbing={isScrubbing}
      onSeek={handleSeek}
      onSeekingStart={onSeekingStart}
      onSeekingEnd={onSeekingEnd}
      onSeekableStartReached={onSeekableStartReached}
      onSeekableEndReached={onSeekableEndReached}
      onKeyDown={onKeyDown}
      onArrowPress={onArrowPress}
      onBecameFocused={onBecameFocused}
      onBecameBlurred={onBecameBlurred}
    >
      {useMemo(() => (
        <>
          <FocusableProgressBar.ProgressFill />
          <FocusableProgressBar.SeekableEndFill />
          <FocusableProgressBar.SeekableEndDot>
            <FormattedMessage id="common.live" />
          </FocusableProgressBar.SeekableEndDot>
          <FocusableProgressBar.SeekableStartFill flat />
          {seekableStartDot}
          <FocusableProgressBar.SeekingFill />
          <FocusableProgressBar.SeekingDot tooltipBgColor="transparent" />
          <FocusableProgressBar.MouseOver tooltipBgColor="transparent" />
        </>
      ), [seekableStartDot])}
    </FocusableProgressBar>
  );
}

LiveFocusableProgressBar.propTypes = {
  className: PropTypes.string,
  displaySeekableStartMessage: PropTypes.bool,
  scrubbing: PropTypes.shape({
    direction: PropTypes.oneOf(Object.values(ScrubbingDirection)),
    speed: PropTypes.oneOf(Object.values(ScrubbingSpeed)),
  }),
  isScrubbing: PropTypes.bool,
  onSeekingStart: PropTypes.func,
  onSeekingEnd: PropTypes.func,
  onSeekableStartReached: PropTypes.func,
  onSeekableEndReached: PropTypes.func,
  onKeyDown: PropTypes.func,
  onArrowPress: PropTypes.func,
  onBecameFocused: PropTypes.func,
  onBecameBlurred: PropTypes.func,
};

LiveFocusableProgressBar.defaultProps = {
  className: '',
  displaySeekableStartMessage: false,
  scrubbing: undefined,
  isScrubbing: false,
  onSeekingStart: undefined,
  onSeekingEnd: undefined,
  onSeekableStartReached: undefined,
  onSeekableEndReached: undefined,
  onKeyDown: undefined,
  onArrowPress: undefined,
  onBecameFocused: undefined,
  onBecameBlurred: undefined,
};

export default memo(LiveFocusableProgressBar);
